import { ColumnDef, Row } from "@tanstack/react-table";
import { SortingTypes } from "../../../helpers/SortingTypes";
import { BaseTable } from "@/components/BaseTable/BaseTable";
import { IVacancyApplication } from "../../../api/vacancyApplications/types";
import { useState, useMemo } from "react";

export interface CandidateTableProps<T extends { id: string | number } = IVacancyApplication> {
  list: T[];
  columns: ColumnDef<T, any>[];
  onRowClick?: (row: Row<T>) => void;
  canSelect?: boolean;
  maxHeight?: string;
  minHeight?: string;
  sortHandler?: (column: { id: string; direction: SortingTypes | null | undefined }) => void;
  sortState?: { column: string; direction: SortingTypes | null | undefined };
  isLoading?: boolean;
}

export const CandidateTable = <T extends { id: string | number } = IVacancyApplication>({
  list,
  columns,
  onRowClick,
  canSelect = false,
  maxHeight = "68vh",
  minHeight = "500px",
  sortHandler,
  sortState,
  isLoading = false,
}: CandidateTableProps<T>) => {
  const [search, setSearch] = useState<string>("");

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  // Функция для поиска по всем полям объекта
  const searchInObject = (obj: any, searchTerm: string): boolean => {
    if (!obj || !searchTerm) return true;
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    const searchRecursively = (value: any): boolean => {
      if (value === null || value === undefined) return false;
      
      if (typeof value === 'string') {
        return value.toLowerCase().includes(lowerSearchTerm);
      }
      
      if (typeof value === 'number') {
        return value.toString().includes(lowerSearchTerm);
      }
      
      if (Array.isArray(value)) {
        return value.some(item => searchRecursively(item));
      }
      
      if (typeof value === 'object') {
        return Object.values(value).some(val => searchRecursively(val));
      }
      
      return false;
    };
    
    return searchRecursively(obj);
  };

  // Фильтрация списка на основе поискового запроса
  const filteredList = useMemo(() => {
    if (!search.trim()) return list;
    
    return list.filter(item => searchInObject(item, search));
  }, [list, search]);

  return (
    <BaseTable
      list={filteredList}
      columns={columns}
      maxHeight={maxHeight}
      minHeight={minHeight}
      canSelect={canSelect}
      rowClick={onRowClick}
      sortHandler={sortHandler}
      sortState={sortState}
      isLoading={isLoading}
      withoutNavigate={true}
    />
  );
};
